import { getStoredUserRole } from '@/lib/staff-roles'
/**
 * Product form → "Pricing & hours" (F-DEAL-N2): special prices by order type,
 * day and time, and the hours the item can be sold. Saved on its own button
 * so the product's main save path is untouched.
 */
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { toastHelpers } from '@/lib/toast-helpers'
import type { PriceRuleInput, ProductPricing, WindowInput } from '@/types/deals'
import { DayToggles, Field, OrderTypeChips, TimeRangeFields, WindowListEditor, useOrderTypeOptions } from './DealsControls'
import { errorText, priceRuleProblem, windowProblem, type DraftWindow } from './dealsText'

interface RuleDraft extends DraftWindow {
  key: string
  label: string
  price: string
  order_types: string[]
  timed: boolean
  is_active: boolean
}

let seq = 0
const k = () => `r${++seq}`

function draftsFrom(p: ProductPricing | undefined): { rules: RuleDraft[]; windows: DraftWindow[] } {
  if (!p) return { rules: [], windows: [] }
  return {
    rules: p.price_rules.map((r) => ({
      key: k(),
      label: r.label ?? '',
      price: String(r.price),
      order_types: [...(r.order_types ?? [])],
      days: [...(r.days ?? [])],
      timed: r.start_minute != null && r.end_minute != null,
      start_minute: r.start_minute,
      end_minute: r.end_minute,
      is_active: r.is_active,
    })),
    windows: p.availability.map((w) => ({ days: [...(w.days ?? [])], start_minute: w.start_minute, end_minute: w.end_minute })),
  }
}

const snapshot = (rules: RuleDraft[], windows: DraftWindow[]) =>
  JSON.stringify([rules.map(({ key: _key, ...r }) => r), windows])

export function ProductPricingSection({
  productId,
  listPrice,
  money,
}: {
  productId: string
  listPrice: number
  money: (v: number) => string
}) {
  const qc = useQueryClient()
  const orderTypes = useOrderTypeOptions()
  // Owner decision 2026-11-02: special prices change what every guest pays,
  // so only an admin may edit them (the server enforces it too).
  const isAdmin = getStoredUserRole() === 'admin'
  const query = useQuery({
    queryKey: ['product-pricing', productId],
    queryFn: async () => (await apiClient.getProductPricing(productId)).data as ProductPricing,
  })
  const [rules, setRules] = useState<RuleDraft[]>([])
  const [windows, setWindows] = useState<DraftWindow[]>([])
  const [baseline, setBaseline] = useState(snapshot([], []))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!query.data) return
    const d = draftsFrom(query.data)
    setRules(d.rules)
    setWindows(d.windows)
    setBaseline(snapshot(d.rules, d.windows))
  }, [query.data])

  const dirty = snapshot(rules, windows) !== baseline
  const update = (key: string, patch: Partial<RuleDraft>) => setRules((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))

  const save = async () => {
    setError(null)
    const body: { price_rules: PriceRuleInput[]; availability: WindowInput[] } = { price_rules: [], availability: [] }
    for (const r of rules) {
      const price = r.price.trim() === '' ? null : Number(r.price)
      const start = r.timed ? r.start_minute : null
      const end = r.timed ? r.end_minute : null
      const days = r.days.length === 7 ? [] : r.days
      const problem = priceRuleProblem({ price, order_types: r.order_types, days, start_minute: start, end_minute: end })
      if (problem) return setError(r.label ? `${r.label}: ${problem}` : problem)
      body.price_rules.push({
        label: r.label.trim(),
        price: price as number,
        order_types: r.order_types,
        days,
        start_minute: start,
        end_minute: end,
        is_active: r.is_active,
      })
    }
    for (const w of windows) {
      const problem = windowProblem(w)
      if (problem) return setError(problem)
      body.availability.push({ days: w.days.length === 7 ? [] : w.days, start_minute: w.start_minute as number, end_minute: w.end_minute as number })
    }
    setSaving(true)
    try {
      await apiClient.putProductPricing(productId, body)
      await qc.invalidateQueries({ queryKey: ['product-pricing', productId] })
      qc.invalidateQueries({ queryKey: ['menu-rules'] })
      toastHelpers.success('Pricing saved')
    } catch (e) {
      setError(errorText(e, 'Could not save pricing'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-base font-semibold">Pricing &amp; hours</h3>
        <p className="text-sm text-muted-foreground">
          Special prices for an order type, some days or some hours (happy hour, a Foodpanda price), and the hours this item can be sold. The menu price is {money(listPrice)}.
        </p>
      </div>

      {query.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-destructive">Could not load this item’s pricing.</p>
      ) : (
        <fieldset disabled={!isAdmin} className="m-0 min-w-0 space-y-4 border-0 p-0">
          {!isAdmin && (
            <p className="text-sm text-muted-foreground">Only an admin can change special prices and item hours.</p>
          )}
          <div className="space-y-3">
            <p className="text-sm font-medium">Special prices</p>
            {rules.length === 0 && <p className="text-sm text-muted-foreground">None — the menu price always applies.</p>}
            {rules.map((r) => (
              <div key={r.key} className={cn('space-y-3 rounded-lg border p-4', !r.is_active && 'opacity-70')}>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[12rem] flex-1">
                    <Field label="Label">
                      <Input value={r.label} maxLength={60} placeholder="Happy hour" onChange={(e) => update(r.key, { label: e.target.value })} />
                    </Field>
                  </div>
                  <div className="w-36">
                    <Field label="Price">
                      <Input type="number" min={0} value={r.price} placeholder="0" onChange={(e) => update(r.key, { price: e.target.value })} />
                    </Field>
                  </div>
                  <div className="flex h-10 items-center gap-2">
                    <Switch checked={r.is_active} aria-label="Rule on" onCheckedChange={(v) => update(r.key, { is_active: v })} />
                    <span className="text-sm text-muted-foreground">{r.is_active ? 'On' : 'Off'}</span>
                  </div>
                  <Button type="button" variant="ghost" size="icon" aria-label="Remove special price" onClick={() => setRules((rs) => rs.filter((x) => x.key !== r.key))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Field label="Order types">
                  <OrderTypeChips options={orderTypes.options} value={r.order_types} onChange={(order_types) => update(r.key, { order_types })} />
                </Field>
                <Field label="Days">
                  <DayToggles value={r.days} onChange={(days) => update(r.key, { days })} />
                </Field>
                <Field label="Hours">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={r.timed} aria-label="Only between set hours" onCheckedChange={(v) => update(r.key, { timed: v, start_minute: r.start_minute ?? 17 * 60, end_minute: r.end_minute ?? 19 * 60 })} />
                      <span className="text-sm text-muted-foreground">{r.timed ? 'Only between' : 'All day'}</span>
                    </div>
                    {r.timed && (
                      <TimeRangeFields idPrefix={`rule-${r.key}`} start={r.start_minute} end={r.end_minute} onChange={(s, e) => update(r.key, { start_minute: s, end_minute: e })} />
                    )}
                  </div>
                </Field>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                setRules((rs) => [
                  ...rs,
                  { key: k(), label: '', price: '', order_types: [], days: [], timed: true, start_minute: 17 * 60, end_minute: 19 * 60, is_active: true },
                ])
              }
            >
              <Plus className="h-4 w-4" />
              Add special price
            </Button>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium">Available only at these times</p>
            <WindowListEditor value={windows} onChange={setWindows} emptyText="No limits — the item can be sold whenever you are open." />
          </div>

          <p className="text-xs text-muted-foreground">
            The most specific rule wins (a timed rule beats an all-day one; a channel rule beats an every-channel one); if two tie, the lower price wins. A rung-up line keeps its price after the window ends.
          </p>

          {error && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button type="button" variant={dirty ? 'default' : 'outline'} disabled={!dirty || saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save pricing'}
            </Button>
            {dirty && <span className="text-xs text-muted-foreground">Pricing has unsaved changes</span>}
          </div>
        </fieldset>
      )}
    </div>
  )
}
